import { useState, useMemo } from 'react';
import { Check, ChevronDown, Award } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { WAITLIST } from '@/constants/testIds';
import {
  CERT_SECTORS,
  SECTOR_LABELS,
  CERTIFICATIONS_DATA,
  CERTIFICATIONS_BY_ID,
  localLabel,
} from '@/i18n/certifications';

// Industry options: keep order from certifications.js but ensure "all" is first.
const INDUSTRY_ORDER = ['all', 'food', 'cosmetics', 'fashion', 'electronics', 'cross'];
const INDUSTRY_OPTIONS = INDUSTRY_ORDER.filter((s) => CERT_SECTORS.includes(s));

function safeId(id) {
  return String(id).replace(/\s+/g, '-').toLowerCase();
}

function CertOptionRow({ cert, locale, checked, onToggle }) {
  const label = localLabel(cert, locale);
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={checked}
        data-testid={`${WAITLIST.certOptionPrefix}-${safeId(cert.id)}`}
        onClick={onToggle}
        className={`w-full flex items-start gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
          checked ? 'bg-primary/10' : 'hover:bg-secondary/60'
        }`}
      >
        <span
          className={`mt-0.5 h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${
            checked ? 'bg-primary border-primary' : 'border-border bg-background'
          }`}
          aria-hidden="true"
        >
          {checked && <Check className="h-3 w-3 text-primary-foreground" strokeWidth={3} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-medium text-foreground/95">{label}</span>
          {cert.ref && (
            <span className="block mt-0.5 text-[11px] text-muted-foreground">
              {cert.ref}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

function GroupedCertList({ certs, locale, selected, onToggle, sectorLabelFn }) {
  // Used when industry === 'all': group certifications by sector with headers.
  const order = ['food', 'cosmetics', 'fashion', 'electronics', 'cross'];
  const groups = new Map();
  for (const c of certs) {
    if (!groups.has(c.sector)) groups.set(c.sector, []);
    groups.get(c.sector).push(c);
  }
  return (
    <div className="py-1">
      {order
        .filter((s) => groups.has(s))
        .map((sector) => (
          <div key={sector}>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-primary border-y border-border bg-secondary/40">
              {sectorLabelFn(sector)}
            </div>
            <ul role="listbox" aria-label={sectorLabelFn(sector)}>
              {groups.get(sector).map((c) => (
                <CertOptionRow
                  key={c.id}
                  cert={c}
                  locale={locale}
                  checked={selected.includes(c.id)}
                  onToggle={() => onToggle(c.id)}
                />
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

function SelectedChips({ ids, locale, onToggle, removeLabel }) {
  if (!ids.length) return null;
  return (
    <div
      data-testid="waitlist-cert-chips"
      className="flex flex-wrap items-center gap-1.5 pt-3 mt-3 border-t border-border"
    >
      <Award className="h-3.5 w-3.5 text-primary" />
      {ids.map((id) => {
        const cert = CERTIFICATIONS_BY_ID[id];
        const label = cert ? localLabel(cert, locale) : id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onToggle(id)}
            data-testid={`waitlist-cert-chip-${safeId(id)}`}
            className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-1 text-[11px] text-foreground hover:bg-primary/20 transition-colors"
            title={removeLabel || 'Remove'}
            aria-label={`${removeLabel || 'Remove'}: ${label}`}
          >
            {label}
            <span aria-hidden className="text-primary text-sm leading-none -mt-px">
              ×
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Industry-first certifications picker.
 *  1) User selects an industry from a dropdown.
 *  2) A multi-select popover shows the relevant certifications.
 *  3) Selected certifications appear as removable chips below.
 *
 * Props:
 *  - selected: string[] of canonical cert ids
 *  - locale: 'en' | 'de'
 *  - onToggle(id): toggle a cert id in selection
 *  - labels: { industry, industryPlaceholder, picker, pickerPlaceholder,
 *              selectedOne, selectedMany (template '{n} ...'),
 *              emptyForIndustry, pickIndustryFirst, remove }
 */
export default function CertificationsPicker({
  selected,
  locale,
  onToggle,
  labels,
}) {
  const [industry, setIndustry] = useState('');
  const [open, setOpen] = useState(false);

  const sectorLabel = (s) =>
    (SECTOR_LABELS[locale] && SECTOR_LABELS[locale][s]) || SECTOR_LABELS.en[s];

  const visibleCerts = useMemo(() => {
    if (!industry) return [];
    if (industry === 'all') return CERTIFICATIONS_DATA;
    return CERTIFICATIONS_DATA.filter((c) => c.sector === industry);
  }, [industry]);

  const triggerText = (() => {
    if (selected.length === 0) return labels.pickerPlaceholder;
    if (selected.length === 1) return labels.selectedOne;
    return (labels.selectedMany || '{n}').replace('{n}', String(selected.length));
  })();

  return (
    <div data-testid={WAITLIST.certifications} className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        {/* Step 1 — Industry */}
        <div>
          <Label
            htmlFor="picker-industry"
            className="text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            {labels.industry}
          </Label>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger
              id="picker-industry"
              data-testid={WAITLIST.industry}
              className="mt-1.5 h-11 bg-secondary/50 border-border focus:ring-ring"
            >
              <SelectValue placeholder={labels.industryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRY_OPTIONS.map((s) => (
                <SelectItem
                  key={s}
                  value={s}
                  data-testid={`waitlist-industry-option-${s}`}
                >
                  {sectorLabel(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Step 2 — Certifications multi-select */}
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">
            {labels.picker}
          </Label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={!industry}
                data-testid={WAITLIST.certsTrigger}
                className="mt-1.5 h-11 w-full justify-between bg-secondary/50 border-border hover:bg-secondary/70 font-normal disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="truncate text-foreground/90">
                  {industry ? triggerText : labels.pickIndustryFirst}
                </span>
                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 ml-2" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={6}
              className="p-0 w-[var(--radix-popover-trigger-width)] max-h-[320px] overflow-auto bg-card border border-border shadow-lg"
              data-testid="waitlist-cert-popover"
            >
              {visibleCerts.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">
                  {labels.emptyForIndustry}
                </div>
              ) : industry === 'all' ? (
                <GroupedCertList
                  certs={visibleCerts}
                  locale={locale}
                  selected={selected}
                  onToggle={onToggle}
                  sectorLabelFn={sectorLabel}
                />
              ) : (
                <ul role="listbox" aria-label={sectorLabel(industry)} className="py-1">
                  {visibleCerts.map((c) => (
                    <CertOptionRow
                      key={c.id}
                      cert={c}
                      locale={locale}
                      checked={selected.includes(c.id)}
                      onToggle={() => onToggle(c.id)}
                    />
                  ))}
                </ul>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <SelectedChips
        ids={selected}
        locale={locale}
        onToggle={onToggle}
        removeLabel={labels.remove}
      />
    </div>
  );
}
